/**
 * WHICH BUILD, AND WHICH ENVIRONMENT, AM I LOOKING AT.
 *
 * Blue/green means two apps on two domains at once (`trade.amaksimov.com` and
 * `trade.dev.amaksimov.com`), open side by side in two tabs. A commit hash alone does not
 * answer the question that matters in that situation -- "is this the dev one?" -- so the
 * environment travels WITH the build, always, and never separately.
 *
 * BOTH VALUES ARE BUILD-TIME. Vite inlines any `VITE_*` variable present when
 * `npm run build` runs, and the pipeline sets them per stage from
 * `CODEBUILD_RESOLVED_SOURCE_VERSION` (see infra-cdk/lib/pipeline-stack.ts). There is no
 * runtime lookup, deliberately: a value fetched at runtime tells you what the SERVER
 * thinks, not which bundle the browser is executing, and the point of this field is to
 * identify the bundle in front of you.
 *
 * A SEPARATE FIELD FROM `ict.version`. `/api/health` already reports the ict/ library
 * version; conflating "which library" with "which commit" in one field is this repo's
 * recurring defect, so `build` is its own thing on both sides.
 */

/** What the bundle knows about itself. Both fields are always present, never optional. */
export type BuildInfo = {
  /** First 8 chars of the commit hash, or `local` when built outside the pipeline. */
  readonly build: string;
  /** `prod`, `dev`, or `local`. */
  readonly appEnv: string;
};

/**
 * Read the two injected values, defaulting honestly.
 *
 * `local` rather than `unknown` for a missing value: a bundle built by `npm run dev` on
 * the dev machine genuinely IS a local build, and calling that "unknown" would make the
 * common case look like a fault.
 */
export function readBuildInfo(env: Record<string, string | undefined>): BuildInfo {
  const raw = (env.VITE_APP_BUILD ?? '').trim();
  return {
    // TRUNCATED HERE TOO, not only in the pipeline. The pipeline passes 8 chars, but a
    // hand-run `VITE_APP_BUILD=$(git rev-parse HEAD)` passes 40. The reason is no longer the
    // 300px sidebar -- the stamp moved into the HEADER strip on 2026-08-18 (card t_677775e0) --
    // it is that the header is a single `flex-wrap` row shared with the source/asset selectors,
    // the drawing toolbar and the status line, so 32 extra characters push a control onto a
    // second row and cost the charts that height. 8 chars is also what `/api/health` reports,
    // so a longer local value would make `buildMismatch` compare unequal lengths.
    build: raw ? raw.slice(0, 8) : 'local',
    appEnv: (env.VITE_APP_ENV ?? '').trim() || 'local',
  };
}

/**
 * The one-line form, for the tab title.
 *
 * ENVIRONMENT FIRST, because that is the field being scanned for when two tabs are open;
 * the hash is the detail you read second, once you know which app you are on. The TITLE
 * matters more than it sounds: with prod and dev side by side the tab strip is the only
 * place both are visible at once.
 */
export function buildLabel(info: BuildInfo): string {
  return `${info.appEnv} · ${info.build}`;
}

/**
 * Does the served bundle match the backend that is answering it?
 *
 * A CloudFront cache that kept an old index.html, or an s3 sync that half-completed,
 * produces exactly this: a bundle from one commit talking to a Lambda from another. It is
 * invisible without comparing the two, and it is the failure that makes "the deploy did
 * nothing" reports irreproducible. Empty/unknown backend values are NOT a mismatch -- a
 * local API has no build stamp and must not paint a warning.
 */
export function buildMismatch(info: BuildInfo, apiBuild: string | undefined): string | null {
  const api = (apiBuild ?? '').trim().slice(0, 8);
  if (!api || api === 'local' || api === info.build) return null;
  return api;
}
